// Утилита клиентского поиска для таблиц ENPS-дашборда (исполнители/авторы/категории). Общая,
// а не по одной копии на компонент: правило нормализации и совпадения токенов должно быть
// одинаковым везде, иначе «ив» находил бы разные строки в разных вкладках.

// Нормализация перед сравнением: регистр, ё/е и пробелы не должны влиять на совпадение —
// «Иванов» и «иванов», «ё» и «е» в фамилии, лишние пробелы при вставке — всё одно и то же.
export function normalizeString(value) {
    return (value || '').toLowerCase().replace(/ё/g, 'е').trim().replace(/\s+/g, ' ');
}

// Запрос разбивается на токены по пробелу, и текст должен содержать КАЖДЫЙ из них — но не
// обязательно подряд и не обязательно в том же порядке. Поэтому «иванов ив» и «ив иванов»
// дают одинаковый результат: оба токена — подстроки текста, порядок токенов не проверяется.
export function matchesQuery(text, query) {
    const normalizedText = normalizeString(text);
    const normalizedQuery = normalizeString(query);

    if (!normalizedQuery) {
        return true;
    }

    return normalizedQuery.split(' ').every((token) => token && normalizedText.includes(token));
}

// Транслитерация ТОЛЬКО кириллица → латиница — намеренно одностороннее решение. Обратное
// направление (латиница → кириллица) принципиально неоднозначно на уровне отдельных букв
// («s» — это «с» или начало «sh»/«sch»?) и потребовало бы перебора вариантов. Сведение обеих
// сторон к одному (латинскому) алфавиту снимает эту неоднозначность вообще: кириллическое «ш»
// разворачивается в «sh», латинское «sh» остаётся «sh» как есть — они сходятся сами.
const CYRILLIC_TO_LATIN = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'y',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'ts',
    ч: 'ch',
    ш: 'sh',
    щ: 'shch',
    ъ: '',
    ы: 'y',
    ь: '',
    э: 'e',
    ю: 'yu',
    я: 'ya',
};

// Свёртка строки к одному алфавиту для сравнения без учёта регистра, ё/е, транслита и
// пунктуации: нижний регистр → ё→е → транслит кириллицы в латиницу → всё, кроме [a-z0-9],
// выбрасывается. «ТПРО.рф», «Т.Про», «ТПро», «TPro», «T.pro» — после свёртки одна и та же
// строка «tpro» (у «ТПРО.рф» — «tprorf»), поэтому подстрочный поиск после свёртки находит их
// все независимо от исходного алфавита и пунктуации запроса/названия.
export function fold(value) {
    const lowered = (value || '').toLowerCase().replace(/ё/g, 'е');
    const transliterated = lowered
        .split('')
        .map((char) => (char in CYRILLIC_TO_LATIN ? CYRILLIC_TO_LATIN[char] : char))
        .join('');

    return transliterated.replace(/[^a-z0-9]/g, '');
}

// Таблица физического соответствия клавиш ЙЦУКЕН↔QWERTY (та же классика, из-за которой
// «привет», набранный при английской раскладке, превращается в «ghbdtn»). Соответствие
// строго 1:1 по позиции клавиши, поэтому таблица одна и обращается программно — в отличие от
// транслита здесь нет неоднозначных диграфов и не нужно гадать направление.
const QWERTY_TO_YCUKEN = {
    'q': 'й',
    'w': 'ц',
    'e': 'у',
    'r': 'к',
    't': 'е',
    'y': 'н',
    'u': 'г',
    'i': 'ш',
    'o': 'щ',
    'p': 'з',
    '[': 'х',
    ']': 'ъ',
    'a': 'ф',
    's': 'ы',
    'd': 'в',
    'f': 'а',
    'g': 'п',
    'h': 'р',
    'j': 'о',
    'k': 'л',
    'l': 'д',
    ';': 'ж',
    "'": 'э',
    'z': 'я',
    'x': 'ч',
    'c': 'с',
    'v': 'м',
    'b': 'и',
    'n': 'т',
    'm': 'ь',
    ',': 'б',
    '.': 'ю',
    '`': 'ё',
};

const YCUKEN_TO_QWERTY = Object.entries(QWERTY_TO_YCUKEN).reduce((map, [latin, cyrillic]) => {
    map[cyrillic] = latin;

    return map;
}, {});

// Переключение раскладки в обе стороны, посимвольно, с сохранением регистра исходного
// символа. Символы, которых нет ни в одной из раскладок (цифры, уже смешанный текст),
// остаются как есть — это осознанно: свёртка fold() всё равно выбросит их, если они не
// [a-z0-9], а портить то, что не относится к буквам раскладки, незачем.
export function swapLayout(value) {
    return (value || '')
        .split('')
        .map((char) => {
            const lower = char.toLowerCase();
            const swapped = QWERTY_TO_YCUKEN[lower] ?? YCUKEN_TO_QWERTY[lower];

            if (!swapped) {
                return char;
            }

            return char === lower ? swapped : swapped.toUpperCase();
        })
        .join('');
}

// Ранжирование опций по одному запросу, без обмена раскладки — это отдельный шаг, а не
// финальная точка входа (см. filterOptions ниже). Порядок — от самого точного совпадения к
// самому слабому, каждая опция попадает ровно в один, самый сильный подходящий ей уровень:
//   1. точное совпадение (без учёта регистра);
//   2. название начинается с запроса;
//   3. запрос — подстрока названия (обычное сравнение, без свёртки);
//   4. запрос находится только после свёртки обеих сторон (см. fold) — сюда попадают случаи
//      смены алфавита/транслита/пунктуации, которые уровни 1-3 не ловят.
// getFoldedText обязан отдавать УЖЕ свёрнутую строку (посчитанную заранее, при построении
// списка опций) — здесь fold() вызывается только для запроса, один раз за проход, а не для
// каждой опции на каждое нажатие клавиши.
export function rankMatches(options, query, { getText, getFoldedText }) {
    if (!query) {
        return options.slice();
    }

    const lowerQuery = query.toLowerCase();
    const foldedQuery = fold(query);

    const exact = [];
    const prefix = [];
    const substring = [];
    const folded = [];

    options.forEach((option) => {
        const text = getText(option) || '';
        const lowerText = text.toLowerCase();

        if (lowerText === lowerQuery) {
            exact.push(option);
        } else if (lowerText.startsWith(lowerQuery)) {
            prefix.push(option);
        } else if (lowerText.includes(lowerQuery)) {
            substring.push(option);
        } else if (foldedQuery && (getFoldedText(option) || '').includes(foldedQuery)) {
            // foldedQuery может оказаться пустым, если запрос — сплошная пунктуация: пустая
            // строка — подстрока чего угодно, без этой проверки такой запрос ложно совпал бы
            // со всем списком.
            folded.push(option);
        }
    });

    return [...exact, ...prefix, ...substring, ...folded];
}

// Точка входа для :filter у vue-select. Обмен раскладки — второй проход, и он делается,
// только если первый (без обмена, но уже со свёрткой — уровни 1-4 rankMatches) не нашёл в
// списке вообще ничего: раскладка — это гипотеза «пользователь напечатал не в том алфавите»,
// и проверять её имеет смысл только тогда, когда обычное прочтение запроса ничего не дало.
export function filterOptions(options, query, accessors) {
    if (!query) {
        return options.slice();
    }

    const direct = rankMatches(options, query, accessors);

    if (direct.length > 0) {
        return direct;
    }

    const swapped = swapLayout(query);

    if (swapped === query) {
        return direct;
    }

    return rankMatches(options, swapped, accessors);
}
